import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { Virtuoso } from 'react-virtuoso';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRightSmall, ChatFill, LikeFill } from '@peacecover/icon';
import { useSceneScrollManager } from './hooks/useSceneScrollManager';
import { getContentKey, useSceneChatList } from './hooks/useScenePaginationList';
import {
  SCENE_READY_POLL_INTERVAL_MS,
  useGetSceneDetail
} from '../../domains/scene/ui/hooks/useGetSceneDetail';
import { useLikeAdaptedFictionEdition } from '../../domains/scene/ui/hooks/useLikeAdaptedFictionEdition';
import { useMarqueeAnimation } from '../chat/hooks/useMarqueeAnimation';
import { useHandleHistoryBack } from '../../utils/useHandleHistoryBack';
import { getCastRelationshipId } from './utils/getCastRelationshipId';
import ThreadContentItem from './components/ThreadContentItem';
import PreviewHeaderItem from './components/PreviewHeaderItem';
import PreviewCastRelationship from './components/PreviewCastRelationship';
import CommentBottomSheet from './components/CommentBottomSheet';
import HeartPopup from './components/HeartPopup';
import SceneOriginalProductList from './components/SceneOriginalProductList';
import BettieChargeBottomSheet from '../../components/bottomSheet/bettie/BettieChargeBottomSheet';
import LoadingSceneContentsOverlay from './components/LoadingSceneContentsOverlay';
import SettingThirdPersonaPopup from './components/SettingThirdPersonaPopup';
import { ReactComponent as GhostProfileIcon } from '../../icons/thumbnail.ghost.profile.svg';
import { ROUTES } from '../../constants/routes';
import type { components } from '../../types/schema';

type SceneDetail = components['schemas']['ApiV1Scene'];
type SceneContent =
  | components['schemas']['ApiV1SceneChat']
  | components['schemas']['ApiV1SceneThread'];

const STAR_HIDE_TIMEOUT = 2500;

const ScenePreviewDetailPage = () => {
  const { t } = useTranslation();
  const { sceneId } = useParams();
  const queryClient = useQueryClient();
  const [isCommentSheetOpen, setIsCommentSheetOpen] = useState(false);
  const [isHeartPopupOpen, setIsHeartPopupOpen] = useState(false);
  const [isSettingThirdPersonaPopupOpen, setIsSettingThirdPersonaPopupOpen] = useState(false);
  const [isBettieSheetOpen, setIsBettieSheetOpen] = useState(false);
  const [showStarForIndicesFrom, setShowStarForIndicesFrom] = useState<number | null>(null);
  const starHideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [refetchInterval, setRefetchInterval] = useState<number | false>(false);
  const { data: sceneDetail, refetch: refetchSceneDetail } = useGetSceneDetail(sceneId ?? '', {
    refetchInterval
  });

  useEffect(() => {
    if (sceneDetail) {
      const shouldPoll =
        sceneDetail.status !== 'completed' && sceneDetail.status !== 'primary_ready';
      setRefetchInterval(shouldPoll ? SCENE_READY_POLL_INTERVAL_MS : false);
    }
  }, [sceneDetail]);

  useEffect(() => {
    if (sceneDetail?.personCRequired && !sceneDetail?.personCConfigured) {
      setIsSettingThirdPersonaPopupOpen(true);
    }
  }, [sceneDetail]);

  const isSceneContentLoading =
    !!sceneDetail && (sceneDetail.status === 'pending' || sceneDetail.status === 'generating');
  const { mutateAsync: likeAdaptedFictionEdition } = useLikeAdaptedFictionEdition();
  const castRelationshipId = getCastRelationshipId();

  const {
    contents,
    isBeforeMode,
    isLoading,
    isFetchingPreviousPage,
    isFetchingNextOne,
    forwardCursor,
    loadMoreBefore,
    handleOpenNextPreview,
    baseContentsLength,
    remainingPreviewsCount,
    totalCount,
    resetKey
  } = useSceneChatList(sceneId);

  const hasSequenceOneInList = useMemo(() => {
    return contents.some((content) => content.sequence === 1);
  }, [contents]);

  const { virtuosoRef, hasScrolledRef, markPrependFromBaseLength } = useSceneScrollManager({
    isBeforeMode,
    baseContentsLength,
    isFetchingPreviousPage,
    contentsLength: contents.length
  });

  useEffect(() => {
    void resetKey;
    hasScrolledRef.current = false;
  }, [resetKey, hasScrolledRef]);

  useEffect(() => {
    return () => {
      if (starHideTimeoutRef.current) clearTimeout(starHideTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;

      const { type, feature } = event.data ?? {};

      if (type === 'PAYMENT_SUCCESS' && feature === 'bettie' && sceneId) {
        queryClient.invalidateQueries(['sceneDetail', sceneId]);
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [queryClient, sceneId]);

  useEffect(() => {
    return () => {
      queryClient.invalidateQueries(['sceneSubscriptions']);
    };
  }, [queryClient]);

  const handleLoadMoreBeforeWithScroll = () => {
    if (!isBeforeMode) return;
    markPrependFromBaseLength();
    hasScrolledRef.current = true;
    loadMoreBefore();
  };

  const tags = [...(sceneDetail?.worldview ?? []), ...(sceneDetail?.keywords ?? [])];

  const getLastContentId = (): string | null => {
    if (contents.length === 0) return null;
    const last = contents[contents.length - 1];
    if ('sceneThreadId' in last && last.sceneThreadId) return last.sceneThreadId;
    if ('sceneChatId' in last && (last as { sceneChatId?: string }).sceneChatId) {
      return (last as { sceneChatId: string }).sceneChatId;
    }
    return null;
  };

  const handleLike = async () => {
    if (!sceneDetail?.adaptedFictionEditionId) return;
    const lastContentId = getLastContentId();
    if (!lastContentId) return;
    try {
      const contentType =
        contents.length > 0 && 'sceneThreadId' in contents[contents.length - 1]
          ? ('SceneThread' as const)
          : ('SceneChat' as const);
      const contexts = [
        { context_type: contentType, context_id: lastContentId },
        { context_type: 'CastRelationship' as const, context_id: castRelationshipId }
      ];
      await likeAdaptedFictionEdition({
        adaptedFictionEditionId: sceneDetail.adaptedFictionEditionId,
        body: { engagement_contexts: contexts }
      });
      await refetchSceneDetail();
    } catch {
      // 좋아요 실패 시 무시
    }
  };

  return (
    <div className="flex min-h-screen flex-col overflow-hidden pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] bg-background-dark">
      {isSceneContentLoading && <LoadingSceneContentsOverlay />}
      <Helmet>
        <title>{sceneDetail?.title ?? t('ScenePreviewDetail.pageTitle')}</title>
      </Helmet>
      <Header sceneDetail={sceneDetail ?? null} />
      <main className="relative flex flex-1 flex-col min-h-0 bg-[#000]">
        {isLoading || contents.length === 0 ? null : (
          <div className="absolute inset-0 min-h-0">
            <Virtuoso
              ref={virtuosoRef}
              overscan={300}
              defaultItemHeight={80}
              style={{ height: '100%' }}
              data={contents}
              startReached={isBeforeMode ? handleLoadMoreBeforeWithScroll : undefined}
              atTopStateChange={
                isBeforeMode ? (atTop) => atTop && handleLoadMoreBeforeWithScroll() : undefined
              }
              atBottomThreshold={isBeforeMode ? undefined : 200}
              computeItemKey={(_index, content: SceneContent) => getContentKey(content)}
              itemContent={(index, content: SceneContent) => (
                <ThreadContentItem
                  speaker={content.speaker ?? undefined}
                  text={content.text}
                  showStar={showStarForIndicesFrom !== null && index >= showStarForIndicesFrom}
                  hasLine={
                    totalCount > 0 ? content.sequence < totalCount : index < contents.length - 1
                  }
                />
              )}
              components={{
                Header: () => (
                  <>
                    {isBeforeMode && isFetchingPreviousPage ? (
                      <div className="px-[22px] pt-[12px]">
                        <BeforeTopLoadingSkeleton />
                      </div>
                    ) : null}
                    {hasSequenceOneInList ? (
                      <PreviewHeaderItem
                        tags={tags}
                        summary={sceneDetail?.summary ?? ''}
                        className="mt-[16px] mb-[8px]"
                      />
                    ) : null}
                  </>
                ),
                Footer: () => (
                  <>
                    {remainingPreviewsCount !== 0 && (
                      <OpenNextPreviewCard
                        remainingPreviewsCount={remainingPreviewsCount}
                        speaker={contents.find((content) => content.speaker)?.speaker ?? null}
                        disabled={!forwardCursor || isFetchingNextOne}
                        onClick={() => setIsHeartPopupOpen(true)}
                      />
                    )}
                    <SceneOriginalProductList
                      sceneId={sceneId ?? ''}
                      sceneDetail={sceneDetail ?? null}
                      castRelationshipId={castRelationshipId}
                      isFullAccessPurchased={sceneDetail?.fullAccessPurchased ?? false}
                    />
                  </>
                )
              }}
            />
          </div>
        )}
      </main>
      <SceneStatusBar
        sceneDetail={sceneDetail ?? null}
        handleLike={handleLike}
        handleComment={() => setIsCommentSheetOpen(true)}
      />
      {sceneDetail?.adaptedFictionEditionId && sceneId && (
        <CommentBottomSheet
          adaptedFictionEditionId={sceneDetail.adaptedFictionEditionId}
          sceneId={sceneId}
          isOpen={isCommentSheetOpen}
          onClose={() => setIsCommentSheetOpen(false)}
          snapToBottom
          isDarkMode={true}
        />
      )}
      {isHeartPopupOpen && (
        <HeartPopup
          onOpenNext={async () => {
            const startIndex = contents.length;
            try {
              await handleOpenNextPreview();
              queryClient.invalidateQueries(['bettieBalances']);
              queryClient.invalidateQueries(['adaptedFictionPreviews']);
              if (starHideTimeoutRef.current) clearTimeout(starHideTimeoutRef.current);
              setShowStarForIndicesFrom(startIndex);
              starHideTimeoutRef.current = setTimeout(() => {
                setShowStarForIndicesFrom(null);
                starHideTimeoutRef.current = null;
              }, STAR_HIDE_TIMEOUT);
            } catch {
              console.error('Failed to open next preview');
            } finally {
              setIsHeartPopupOpen(false);
            }
          }}
          onClose={() => setIsHeartPopupOpen(false)}
          onOpenBettieSheet={() => setIsBettieSheetOpen(true)}
        />
      )}
      {isSettingThirdPersonaPopupOpen && (
        <SettingThirdPersonaPopup
          onClose={() => setIsSettingThirdPersonaPopupOpen(false)}
          castRelationshipId={sceneDetail?.castRelationship?.castRelationshipId ?? ''}
        />
      )}
      <BettieChargeBottomSheet
        isOpen={isBettieSheetOpen}
        onClose={() => setIsBettieSheetOpen(false)}
        onetimeOnly
      />
    </div>
  );
};

export default ScenePreviewDetailPage;

const Header = ({ sceneDetail }: { sceneDetail: SceneDetail | null }) => {
  const { t } = useTranslation();
  const handleHistoryBack = useHandleHistoryBack();
  const navigate = useNavigate();
  const title = sceneDetail?.title ?? '';
  const { shouldAnimate, textRef, containerRef } = useMarqueeAnimation(title);
  return (
    <header className="flex flex-col bg-backgroundElevated-dark">
      <div className="flex items-center justify-between w-full px-[16px] py-[8px] relative">
        <button type="button" onClick={handleHistoryBack}>
          <ArrowLeft size={24} className="text-main-dark-1" />
        </button>
        <div
          ref={containerRef}
          className={`max-w-[175px] flex overflow-hidden ${shouldAnimate ? '' : 'justify-center'} absolute left-1/2 transform -translate-x-1/2`}
        >
          {shouldAnimate ? (
            <div className="header-title-marquee flex w-max items-center">
              <div className="flex items-center">
                <p
                  ref={textRef}
                  className="text-[17px] font-bold leading-[20px] text-main-dark-1 whitespace-nowrap"
                >
                  {title}
                </p>
                <span className="w-[16px] shrink-0" aria-hidden="true" />
              </div>
              <div className="flex items-center" aria-hidden="true">
                <p className="text-[17px] font-bold leading-[20px] text-main-dark-1 whitespace-nowrap">
                  {title}
                </p>
                <span className="w-[16px] shrink-0" aria-hidden="true" />
              </div>
            </div>
          ) : (
            <p
              ref={textRef}
              className="text-[17px] font-bold leading-[20px] text-center text-main-dark-1 whitespace-nowrap"
            >
              {title}
            </p>
          )}
        </div>
      </div>
      <div className="px-[24px] py-[14px] flex items-center justify-between text-pure-white">
        <PreviewCastRelationship castRelationship={sceneDetail?.castRelationship ?? null} />
        <button
          type="button"
          className="flex items-center gap-[24px]"
          onClick={() => navigate(ROUTES.SCENE.CAST_RELATIONSHIPS)}
        >
          <div className="w-[1px] h-[24px] bg-sub-dark-2" />
          <p className="text-[12px] font-medium leading-[14px]">{t('ScenePreviewDetail.change')}</p>
        </button>
      </div>
    </header>
  );
};

const OpenNextPreviewCard = ({
  remainingPreviewsCount,
  speaker,
  onClick,
  disabled
}: {
  remainingPreviewsCount: number | null;
  speaker: string | null;
  onClick: () => void;
  disabled: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <div className="text-pure-white px-[22px] py-[15px] flex items-start gap-[10px]">
      <GhostProfileIcon width={26} height={26} className="flex-shrink-0" />
      <div className="flex flex-col items-start gap-[5px] w-full">
        <div className="text-[14px] font-semibold leading-[17px] text-separator-light-1">
          {speaker ?? t('ScenePreviewDetail.anonymous')}
        </div>
        <div className="bg-graident-multi-blue rounded-[16px] w-full p-[1px]">
          <div className="pt-[15px] pl-[15px] pb-[12px] pr-[19px] bg-black rounded-[16px]">
            <p className="text-[11px] font-medium leading-[13px] text-[#B2B2B2]">
              {t('ScenePreviewDetail.leftPreview', { count: remainingPreviewsCount ?? 0 })}
            </p>
            <p className="mt-[5px] text-[15px] font-bold leading-[18px] bg-graident-multi-blue bg-clip-text text-transparent">
              {t('ScenePreviewDetail.openNextPreview')}
            </p>
            <button
              type="button"
              onClick={onClick}
              disabled={disabled}
              className="flex items-center justify-end w-full mt-[8px] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <img
                src="/assets/icons/graphic-3d/3D.like.png"
                alt="Like"
                className="w-[16px] h-[16px]"
              />
              <p className="text-[14px] font-bold leading-[17px] text-pure-white ml-[4px]">
                {t('ScenePreviewDetail.needHearts', { count: 2 })}
              </p>
              <ArrowRightSmall size={17} className="text-pure-white" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const SceneStatusBar = ({
  sceneDetail,
  handleLike,
  handleComment
}: {
  sceneDetail: SceneDetail | null;
  handleLike: () => void;
  handleComment: () => void;
}) => {
  return (
    <div className="fixed bottom-0 left-0 right-0 pb-[calc(env(safe-area-inset-bottom)+10px)] bg-pure-black pt-[16px] rounded-t-[20px] px-[20px] flex items-center justify-start gap-[24px]">
      <button type="button" className="flex flex-col items-center" onClick={handleLike}>
        <LikeFill
          className="text-pure-white"
          gradientStops={
            sceneDetail?.isLiked
              ? [
                  { offset: '0%', color: '#FF45A9' },
                  { offset: '100%', color: '#FF6A6A' }
                ]
              : []
          }
        />
        <p className="text-[11px] font-semibold leading-[13px] text-pure-white">
          {sceneDetail?.likesCount}
        </p>
      </button>
      <button type="button" className="flex flex-col items-center" onClick={handleComment}>
        <ChatFill className="text-pure-white" />
        <p className="text-[11px] font-semibold leading-[13px] text-pure-white">
          {sceneDetail?.commentsCount}
        </p>
      </button>
    </div>
  );
};

const BeforeTopLoadingSkeleton = () => {
  return (
    <output className="flex gap-[10px] items-stretch py-[15px] animate-pulse" aria-live="polite">
      <div className="flex flex-col items-center gap-[8px]">
        <div className="w-[26px] h-[26px] rounded-full bg-white/10 shrink-0" />
        <div className="w-[2px] flex-1 min-h-[40px] rounded-[16px] bg-white/10" />
      </div>
      <div className="flex flex-col gap-[5px] flex-1 min-w-0">
        <div className="h-[17px] w-[120px] rounded bg-white/10" />
        <div className="h-[19px] w-full rounded bg-white/10" />
        <div className="h-[19px] w-[92%] rounded bg-white/10" />
        <div className="h-[19px] w-[78%] rounded bg-white/10" />
      </div>
    </output>
  );
};
